import { Injectable } from '@angular/core'
import { Subject, BehaviorSubject } from 'rxjs'

export interface AudioChunk {
  audioData: string
  sequence: number
  isComplete: boolean
  confidence?: number
  duration?: number
}

export interface AudioLevelInfo {
  currentLevel: number
  isDetectingVoice: boolean
  speechDuration: number
  confidence: number
}

@Injectable({
  providedIn: 'root',
})
export class AudioRecordingService {
  private mediaRecorder: MediaRecorder | null = null
  private stream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private dataArray: Uint8Array<ArrayBuffer> | null = null

  private sequenceNumber = 0
  private isContinuousMode = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private silenceTimeout: any
  private isProcessingAudio = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private audioMonitoringInterval: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private maxRecordingTimeout: any

  private SILENCE_THRESHOLD = 24
  // 音声認識の無音判定時間（ミリ秒）- この時間無音が続くと発話終了と判定
  private SILENCE_DURATION = 1800
  // 最小発話時間（ミリ秒）- これより短い音声は雑音として無視
  private MIN_SPEECH_DURATION = 500
  // 最大録音時間（ミリ秒）- 長時間の録音を防ぐための上限
  private MAX_RECORDING_DURATION = 60000
  private lastSoundTime = 0
  private hasDetectedSound = false
  private speechStartTime = 0
  private volumeHistory: number[] = []

  // 音声設定
  private audioSettings = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: false,
  }

  private audioChunkSubject = new Subject<AudioChunk>()
  private audioLevelSubject = new BehaviorSubject<AudioLevelInfo>({
    currentLevel: 0,
    isDetectingVoice: false,
    speechDuration: 0,
    confidence: 0,
  })

  public audioChunk$ = this.audioChunkSubject.asObservable()
  public audioLevel$ = this.audioLevelSubject.asObservable()

  private processingSubject = new BehaviorSubject<boolean>(false)
  public processing$ = this.processingSubject.asObservable()

  updateVADSettings(settings: {
    silenceThreshold?: number
    silenceDuration?: number
    minSpeechDuration?: number
    maxRecordingDuration?: number
  }): void {
    if (settings.silenceThreshold != null) {
      this.SILENCE_THRESHOLD = Math.max(
        1,
        Math.min(50, settings.silenceThreshold),
      )
    }
    if (settings.silenceDuration != null) {
      this.SILENCE_DURATION = Math.max(
        500,
        Math.min(5000, settings.silenceDuration),
      )
    }
    if (settings.minSpeechDuration != null) {
      this.MIN_SPEECH_DURATION = Math.max(
        100,
        Math.min(2000, settings.minSpeechDuration),
      )
    }
    if (settings.maxRecordingDuration != null) {
      this.MAX_RECORDING_DURATION = Math.max(
        30000,
        Math.min(60000, settings.maxRecordingDuration),
      )
    }
    console.log('VAD settings updated:', {
      silenceThreshold: this.SILENCE_THRESHOLD,
      silenceDuration: this.SILENCE_DURATION,
      minSpeechDuration: this.MIN_SPEECH_DURATION,
      maxRecordingDuration: this.MAX_RECORDING_DURATION,
    })
  }

  getVADSettings(): {
    silenceThreshold: number
    silenceDuration: number
    minSpeechDuration: number
    maxRecordingDuration: number
  } {
    return {
      silenceThreshold: this.SILENCE_THRESHOLD,
      silenceDuration: this.SILENCE_DURATION,
      minSpeechDuration: this.MIN_SPEECH_DURATION,
      maxRecordingDuration: this.MAX_RECORDING_DURATION,
    }
  }

  getAudioSettings(): {
    echoCancellation: boolean
    noiseSuppression: boolean
    autoGainControl: boolean
  } {
    return { ...this.audioSettings }
  }

  async updateAudioSettings(settings: {
    echoCancellation?: boolean
    noiseSuppression?: boolean
    autoGainControl?: boolean
  }): Promise<void> {
    if (settings.echoCancellation != null) {
      this.audioSettings.echoCancellation = settings.echoCancellation
    }
    if (settings.noiseSuppression != null) {
      this.audioSettings.noiseSuppression = settings.noiseSuppression
    }
    if (settings.autoGainControl != null) {
      this.audioSettings.autoGainControl = settings.autoGainControl
    }

    console.log('Audio settings updated:', this.audioSettings)

    // ストリームが既に存在する場合は再起動
    const wasRecording = this.isContinuousMode && this.stream != null
    if (wasRecording) {
      console.log('Restarting audio stream with new settings...')
      await this.restartAudioStream()
    }
  }

  /**
   * エコーキャンセレーションをトグル
   * AudioAnalyzerServiceから移行された便利メソッド
   */
  async toggleEchoCancellation(): Promise<void> {
    this.audioSettings.echoCancellation = !this.audioSettings.echoCancellation
    await this.updateAudioSettings({
      echoCancellation: this.audioSettings.echoCancellation,
    })
    console.log(
      `Echo cancellation: ${this.audioSettings.echoCancellation ? 'ON' : 'OFF'}`,
    )
  }

  /**
   * ノイズサプレッションをトグル
   * AudioAnalyzerServiceから移行された便利メソッド
   */
  async toggleNoiseSuppression(): Promise<void> {
    this.audioSettings.noiseSuppression = !this.audioSettings.noiseSuppression
    await this.updateAudioSettings({
      noiseSuppression: this.audioSettings.noiseSuppression,
    })
    console.log(
      `Noise suppression: ${this.audioSettings.noiseSuppression ? 'ON' : 'OFF'}`,
    )
  }

  /**
   * オートゲインコントロールをトグル
   * AudioAnalyzerServiceから移行された便利メソッド
   */
  async toggleAutoGainControl(): Promise<void> {
    this.audioSettings.autoGainControl = !this.audioSettings.autoGainControl
    await this.updateAudioSettings({
      autoGainControl: this.audioSettings.autoGainControl,
    })
    console.log(
      `Auto gain control: ${this.audioSettings.autoGainControl ? 'ON' : 'OFF'}`,
    )
  }

  private async restartAudioStream(): Promise<void> {
    // 既存のストリームを停止
    this.stopAudioMonitoring()
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }
    this.analyser = null
    this.dataArray = null

    // 音声検出状態をリセット
    this.hasDetectedSound = false
    this.speechStartTime = 0
    this.lastSoundTime = 0
    this.isProcessingAudio = false // 処理中フラグもリセット

    // 新しいストリームを開始
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: this.audioSettings.echoCancellation,
          noiseSuppression: this.audioSettings.noiseSuppression,
          autoGainControl: this.audioSettings.autoGainControl,
        },
      })

      await this.setupAudioAnalysis()

      console.log('Audio stream restarted successfully')
    } catch (error) {
      console.error('Failed to restart audio stream:', error)
      throw error
    }
  }

  async startContinuousRecording(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: this.audioSettings.echoCancellation,
          noiseSuppression: this.audioSettings.noiseSuppression,
          autoGainControl: this.audioSettings.autoGainControl,
        },
      })

      this.isContinuousMode = true

      await this.setupAudioAnalysis()

      this.startNewRecordingSession()
      console.log(
        `🎤 Continuous recording started (max ${this.MAX_RECORDING_DURATION / 1000}s, ${this.SILENCE_DURATION / 1000}s silence detection)`,
      )
    } catch (error) {
      console.error('Failed to start continuous recording:', error)
      throw error
    }
  }

  private async setupAudioAnalysis(): Promise<void> {
    if (!this.stream) return

    this.audioContext = new (
      window.AudioContext ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).webkitAudioContext
    )()
    const source = this.audioContext.createMediaStreamSource(this.stream)
    this.analyser = this.audioContext.createAnalyser()

    this.analyser.fftSize = 1024
    this.analyser.smoothingTimeConstant = 0.3
    this.analyser.minDecibels = -90
    this.analyser.maxDecibels = -10
    this.dataArray = new Uint8Array(this.analyser.frequencyBinCount)

    source.connect(this.analyser)

    this.startAudioMonitoring()
  }

  private startAudioMonitoring(): void {
    this.stopAudioMonitoring()
    this.volumeHistory = []

    this.audioMonitoringInterval = setInterval(() => {
      if (
        !this.analyser ||
        !this.dataArray ||
        !this.isContinuousMode ||
        this.isProcessingAudio
      ) {
        return
      }

      this.analyser.getByteFrequencyData(this.dataArray)

      const volume = this.calculateVolumeLevel()
      this.volumeHistory.push(volume)

      if (this.volumeHistory.length > 100) {
        this.volumeHistory.shift()
      }

      const currentTime = Date.now()
      const confidence = this.calculateConfidence()

      if (volume > this.SILENCE_THRESHOLD) {
        this.lastSoundTime = currentTime
        if (!this.hasDetectedSound) {
          this.hasDetectedSound = true
          this.speechStartTime = currentTime
          console.log('🔊 Speech started')
          this.setMaxRecordingTimeout()
          console.log(
            `⏱️ Max recording timer set to ${this.MAX_RECORDING_DURATION}ms`,
          )
        }
      } else if (
        this.hasDetectedSound &&
        currentTime - this.lastSoundTime >= this.SILENCE_DURATION
      ) {
        const speechDuration = currentTime - this.speechStartTime
        if (speechDuration >= this.MIN_SPEECH_DURATION) {
          console.log('🔇 Speech ended after', speechDuration, 'ms')
          this.processCurrentRecording()
        } else {
          console.log('⚠️ Speech too short, ignoring')
          this.resetSpeechDetection()
        }
      }

      const speechDuration = this.hasDetectedSound
        ? currentTime - this.speechStartTime
        : 0
      this.audioLevelSubject.next({
        currentLevel: volume,
        isDetectingVoice: this.hasDetectedSound,
        speechDuration: speechDuration,
        confidence: confidence,
      })
    }, 50)
  }

  private setMaxRecordingTimeout(): void {
    this.clearMaxRecordingTimeout()
    this.maxRecordingTimeout = setTimeout(() => {
      if (this.hasDetectedSound && !this.isProcessingAudio) {
        const speechDuration = Date.now() - this.speechStartTime
        console.log(
          `⏱️ Max recording duration (${this.MAX_RECORDING_DURATION}ms) reached, processing audio (duration: ${speechDuration}ms)`,
        )
        this.processCurrentRecording()
      }
    }, this.MAX_RECORDING_DURATION)
  }

  private clearMaxRecordingTimeout(): void {
    if (this.maxRecordingTimeout) {
      clearTimeout(this.maxRecordingTimeout)
      this.maxRecordingTimeout = null
    }
  }

  private calculateVolumeLevel(): number {
    if (!this.dataArray) return 0

    let weightedSum = 0
    let totalWeight = 0

    for (let i = 0; i < this.dataArray.length; i++) {
      const frequency =
        (i * this.audioContext!.sampleRate) / (this.analyser!.fftSize * 2)
      let weight = 1

      if (frequency >= 300 && frequency <= 3400) {
        weight = 2
      } else if (frequency >= 85 && frequency <= 255) {
        weight = 0.5
      }

      weightedSum += this.dataArray[i] * weight
      totalWeight += weight
    }

    const weightedAverage = totalWeight > 0 ? weightedSum / totalWeight : 0
    return Math.min(100, (weightedAverage / 255) * 100)
  }

  private calculateConfidence(): number {
    if (this.volumeHistory.length < 5) return 50

    const avgVolume =
      this.volumeHistory.reduce((a, b) => a + b, 0) / this.volumeHistory.length
    const maxVolume = Math.max(...this.volumeHistory)
    const consistency = this.calculateVolumeConsistency()

    const volumeScore = Math.min(avgVolume / 40, 1) * 40
    const peakScore = Math.min(maxVolume / 60, 1) * 30
    const consistencyScore = consistency * 30

    return Math.round(volumeScore + peakScore + consistencyScore)
  }

  private calculateVolumeConsistency(): number {
    if (this.volumeHistory.length < 10) return 0.5

    const mean =
      this.volumeHistory.reduce((a, b) => a + b, 0) / this.volumeHistory.length
    const variance =
      this.volumeHistory.reduce(
        (sum, vol) => sum + Math.pow(vol - mean, 2),
        0,
      ) / this.volumeHistory.length
    const stdDev = Math.sqrt(variance)

    return Math.max(0, 1 - stdDev / 30)
  }

  private resetSpeechDetection(): void {
    this.hasDetectedSound = false
    this.speechStartTime = 0
    this.volumeHistory = []
    this.clearMaxRecordingTimeout()
  }

  private stopAudioMonitoring(): void {
    if (this.audioMonitoringInterval) {
      clearInterval(this.audioMonitoringInterval)
      this.audioMonitoringInterval = null
    }
  }

  private startNewRecordingSession(): void {
    if (!this.stream || !this.isContinuousMode || this.isProcessingAudio) {
      return
    }

    this.sequenceNumber = 0
    this.resetSpeechDetection()

    this.mediaRecorder = new MediaRecorder(this.stream, {
      mimeType: this.getSupportedMimeType(),
    })

    let audioChunks: Blob[] = []

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data)
      }
    }

    this.mediaRecorder.onstop = () => {
      if (!this.isContinuousMode) {
        audioChunks = []
        return
      }

      if (audioChunks.length > 0) {
        const audioBlob = new Blob(audioChunks, {
          type: this.mediaRecorder?.mimeType,
        })
        this.processAudioBlob(audioBlob)
        audioChunks = []
      } else {
        this.onAudioProcessingComplete()
      }
    }

    this.mediaRecorder.onerror = (event) => {
      console.error('❌ MediaRecorder error:', event)
      this.onAudioProcessingComplete()
    }

    this.mediaRecorder.start(100)
  }

  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/mpeg',
    ]

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    return 'audio/webm'
  }

  private processCurrentRecording(): void {
    if (!this.isContinuousMode) {
      return
    }

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.isProcessingAudio = true
      this.processingSubject.next(true)
      this.mediaRecorder.stop()
      this.stopAudioMonitoring()
      this.clearMaxRecordingTimeout()
      this.hasDetectedSound = false
      this.speechStartTime = 0
      this.clearMaxRecordingTimeout()
    }
  }

  private processAudioBlob(audioBlob: Blob): void {
    if (!this.isContinuousMode) {
      return
    }

    if (audioBlob.size === 0) {
      this.onAudioProcessingComplete()
      return
    }

    const reader = new FileReader()

    reader.onloadend = () => {
      if (!this.isContinuousMode) {
        return
      }

      try {
        const base64Audio = (reader.result as string).split(',')[1]

        if (!base64Audio || base64Audio.length === 0) {
          this.onAudioProcessingComplete()
          return
        }

        const currentLevel = this.audioLevelSubject.value
        const duration =
          this.speechStartTime > 0 ? Date.now() - this.speechStartTime : 0

        this.audioChunkSubject.next({
          audioData: base64Audio,
          sequence: 0,
          isComplete: true,
          confidence: currentLevel.confidence,
          duration: duration,
        })
      } catch (error) {
        console.error('❌ Error processing audio:', error)
        this.onAudioProcessingComplete()
      }
    }

    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error)
      this.onAudioProcessingComplete()
    }

    reader.readAsDataURL(audioBlob)
  }

  onAudioProcessingComplete(): void {
    this.isProcessingAudio = false
    this.processingSubject.next(false)
    this.resetSpeechDetection()

    if (this.isContinuousMode && this.stream) {
      setTimeout(() => {
        if (this.isContinuousMode && this.stream) {
          this.startNewRecordingSession()
          this.startAudioMonitoring()
        }
      }, 200)
    }
  }

  stopContinuousRecording(): void {
    this.isContinuousMode = false
    this.isProcessingAudio = false
    this.resetSpeechDetection()

    this.stopAudioMonitoring()
    this.clearSilenceTimer()
    this.clearMaxRecordingTimeout()

    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      const originalOnStop = this.mediaRecorder.onstop
      this.mediaRecorder.onstop = () => {}
      this.mediaRecorder.stop()

      setTimeout(() => {
        if (this.mediaRecorder) {
          this.mediaRecorder.onstop = originalOnStop
        }
      }, 100)
    }

    this.cleanup()
  }

  private clearSilenceTimer(): void {
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout)
      this.silenceTimeout = null
    }
  }

  private cleanup(): void {
    this.stopAudioMonitoring()
    this.clearSilenceTimer()
    this.clearMaxRecordingTimeout()

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    this.analyser = null
    this.dataArray = null

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    this.mediaRecorder = null
    this.sequenceNumber = 0
    this.resetSpeechDetection()

    this.audioLevelSubject.next({
      currentLevel: 0,
      isDetectingVoice: false,
      speechDuration: 0,
      confidence: 0,
    })
  }

  isRecording(): boolean {
    return this.isContinuousMode && !this.isProcessingAudio
  }

  getCurrentAudioLevel(): number {
    return this.audioLevelSubject.value.currentLevel
  }

  getCurrentAudioInfo(): AudioLevelInfo {
    return this.audioLevelSubject.value
  }

  public getFrequencyData(): Uint8Array | null {
    if (!this.analyser || !this.dataArray) {
      return null
    }
    this.analyser.getByteFrequencyData(this.dataArray)
    return this.dataArray
  }

  async startRecording(): Promise<void> {
    throw new Error('Use startContinuousRecording() for this implementation')
  }

  async stopRecording(): Promise<void> {
    throw new Error('Use stopContinuousRecording() for this implementation')
  }

  // testmic
  public async startLevelMonitorOnly(): Promise<void> {
    this.isContinuousMode = true
    if (this.analyser || this.audioContext) {
      return
    }
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: this.audioSettings.echoCancellation,
        noiseSuppression: this.audioSettings.noiseSuppression,
        autoGainControl: this.audioSettings.autoGainControl,
      },
    })
    await this.setupAudioAnalysis()
  }

  // stop testmic
  public stopLevelMonitorOnly(): void {
    this.isContinuousMode = false
    this.stopAudioMonitoring?.()
    this.clearMaxRecordingTimeout()
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }
    this.analyser = null
    this.dataArray = null
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop())
      this.stream = null
    }
  }
}
